import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import DatePicker from "react-datepicker";
import Lottie from 'lottie-react';
import "react-datepicker/dist/react-datepicker.css";
import AddressAutocomplete from '../../../Common/AddressAutocomplete/AddressAutocomplete';
import { getStores } from '../../../../../shared/repos/graphql/store';
import { orderTypeIds } from '../../../../../shared/constants/order';
import ddLog from '../../../../../shared/utilities/datadog/log';

const bakeryAnimation = require("./imgs/bakery.json")
const truckAnimation = require("./imgs/truck.json")

const Event = ({
  formData,
  onFormDataChange,
  onNext
}) => {
  const [orderType, setOrderType] = useState(formData.orderType || 'delivery');
  const [address, setAddress] = useState(formData.address || '');
  const [date, setDate] = useState(formData.date || '');
  const [time, setTime] = useState(formData.time || '');
  const [storeData, setStoreData] = useState(formData.storeData || null);
  const [availableStores, setAvailableStores] = useState([]);
  const [addressCoordinates, setAddressCoordinates] = useState(formData.addressCoordinates || null);

  // Initialize form data with default values if not already set
  useEffect(() => {
    const updates = {};
    if (!formData.orderType) updates.orderType = orderType;
    if (!formData.date) updates.date = new Date(); // Set default date
    if (!formData.time) updates.time = time;

    if (Object.keys(updates).length > 0) {
      onFormDataChange({ ...formData, ...updates });
    }
  }, []);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  const [selectedTimeIndex, setSelectedTimeIndex] = useState(-1);
  const [showStoreDropdown, setShowStoreDropdown] = useState(false);
  const [selectedStoreIndex, setSelectedStoreIndex] = useState(-1);
  const [errorMessage, setErrorMessage] = useState('');

  const timeDropdownRef = useRef(null);
  const storeDropdownRef = useRef(null);

  const handleStoreSelect = (store) => {
    setStoreData(store);
    setShowStoreDropdown(false);
    setSelectedStoreIndex(-1);
  };

  const searchStoresForAddress = async (addressValue, type = null) => {
    // Use coordinates from state or fallback to form data
    const coordinates = addressCoordinates || formData.addressCoordinates;

    if (!coordinates) {
      return;
    }

    try {
      const currentOrderType = type || orderType;

      const response = await getStores({
        lat: coordinates.lat,
        lng: coordinates.lng,
        customAddress: addressValue,
        externalId: coordinates.placeId,
        orderTypeId: currentOrderType === 'delivery' ? orderTypeIds.delivery : orderTypeIds.pickup
      });

      const stores = response.data?.storeSearch?.stores || [];
      setAvailableStores(stores);

      // Select first store by default
      if (stores.length > 0) {
        handleStoreSelect(stores[0]);
      }
    } catch (err) {
      ddLog.logError(err);
    }
  };

  const handleOrderTypeChange = (type) => {
    setOrderType(type);

    // If we have an address and coordinates, search for stores with new orderTypeId
    if (address && addressCoordinates) {
      searchStoresForAddress(address, type);
    }
  };

  const handleAddressChange = (value) => {
    setAddress(value);
    // Clear stores when address changes
    setAvailableStores([]);
    setStoreData(null);
    setShowStoreDropdown(false);
    setSelectedStoreIndex(-1);
  };

  const handleStoreKeyDown = (e) => {
    if (!showStoreDropdown || availableStores.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedStoreIndex(prev => prev < availableStores.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedStoreIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedStoreIndex >= 0 && selectedStoreIndex < availableStores.length) {
          handleStoreSelect(availableStores[selectedStoreIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowStoreDropdown(false);
        break;
      default:
        break;
    }
  };

  const handleStoresLoaded = (stores) => {
    setAvailableStores(stores);
    // Select first store by default
    if (stores.length > 0 && !storeData) {
      handleStoreSelect(stores[0]);
    }
  };

  const handleAddressSelect = (addressData) => {
    const coordinates = {
      lat: addressData.lat,
      lng: addressData.lng,
      placeId: addressData.placeId
    };
    const fullAddress = addressData.address || addressData.formattedAddress;

    // Update local state
    setAddress(fullAddress);
    setAddressCoordinates(coordinates);

    // Clear stores when address changes (selection)
    setAvailableStores([]);
    setStoreData(null);
    setShowStoreDropdown(false);
    setSelectedStoreIndex(-1);
  };


  const handleDateChange = (value) => {
    setDate(value);
  };

  const handleTimeChange = (selectedTime) => {
    const timeValue = selectedTime.value === null ? 'asap' : selectedTime.value;
    setTime(timeValue);
    setShowTimeDropdown(false);
    setSelectedTimeIndex(-1);
  };

  // Generate time slots based on store hours and selected date
  const generateTimeSlots = () => {
    if (!storeData || !date) {
      return [{ label: 'ASAP', value: null }];
    }

    const selectedDate = new Date(date);
    const dayName = selectedDate.toLocaleDateString('en-US', { weekday: 'long' });

    // Get the appropriate hours based on delivery type
    const hoursType = orderType === 'delivery' ? 'Delivery Hours' : 'Retail Hours';

    const storeHours = storeData.hours?.find(h => h.type === hoursType);

    if (!storeHours) {
      return [{ label: 'ASAP', value: null }];
    }

    const dayHours = storeHours.days.find(d => d.day === dayName);
    if (!dayHours) {
      return [{ label: 'ASAP', value: null }];
    }

    // Parse hours (e.g., "11:15 AM - 01:00 AM")
    const [startTime, endTime] = dayHours.hour.split(' - ');

    const parseTime = (timeStr) => {
      const [timePart, period] = timeStr.split(' ');
      const [hours, minutes] = timePart.split(':').map(Number);
      let totalMinutes = hours * 60 + minutes;
      if (period === 'PM' && hours !== 12) totalMinutes += 12 * 60;
      if (period === 'AM' && hours === 12) totalMinutes -= 12 * 60;
      return totalMinutes;
    };

    const startMinutes = parseTime(startTime);
    const endMinutes = parseTime(endTime);

    // Handle overnight hours (e.g., 11 PM - 3 AM)
    const isOvernight = endMinutes < startMinutes;
    const endMinutesAdjusted = isOvernight ? endMinutes + 24 * 60 : endMinutes;

    const slots = [];
    const now = new Date();
    const isToday = selectedDate.toDateString() === now.toDateString();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const minimumMinutes = isToday ? currentMinutes + 60 : 0; // 60 minutes buffer for today

    for (let minutes = startMinutes; minutes < endMinutesAdjusted; minutes += 15) {
      const actualMinutes = minutes % (24 * 60);

      // Skip past slots if it's today
      if (!(isToday && actualMinutes <= minimumMinutes)) {
        const hours = Math.floor(actualMinutes / 60);
        const mins = actualMinutes % 60;
        const period = hours >= 12 ? 'PM' : 'AM';
        let displayHours = hours;
        if (hours === 0) {
          displayHours = 12;
        } else if (hours > 12) {
          displayHours = hours - 12;
        }
        const timeStr = `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
        const valueStr = `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;

        slots.push({ label: timeStr, value: valueStr });
      }
    }

    // If no slots available, return ASAP option
    if (slots.length === 0) {
      return [{ label: 'ASAP', value: null }];
    }

    return slots;
  };

  const handleTimeKeyDown = (e) => {
    const timeSlots = generateTimeSlots();
    if (!showTimeDropdown || timeSlots.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedTimeIndex(prev => prev < timeSlots.length - 1 ? prev + 1 : prev);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedTimeIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedTimeIndex >= 0 && selectedTimeIndex < timeSlots.length) {
          handleTimeChange(timeSlots[selectedTimeIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowTimeDropdown(false);
        break;
      default:
        break;
    }
  };

  const handleClickOutside = (event) => {
    if (timeDropdownRef.current && !timeDropdownRef.current.contains(event.target)) {
      setShowTimeDropdown(false);
    }
    if (storeDropdownRef.current && !storeDropdownRef.current.contains(event.target)) {
      setShowStoreDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  const handleNext = () => {
    setErrorMessage('');

    if (orderType === 'delivery' && !address) {
      setErrorMessage('Please enter a delivery address');
      return;
    }

    if (orderType === 'pickup' && !storeData) {
      setErrorMessage('No nearby store available. Please try a different location.');
      return;
    }

    if (!date || !time) {
      setErrorMessage('Please select both date and time');
      return;
    }

    // Update form data with all current step data before moving to next step
    onFormDataChange({
      orderType,
      address,
      addressCoordinates,
      date,
      time,
      storeData
    });

    onNext();
  };

  const isFormValid = () => {
    if (orderType === 'delivery' && !address) return false;
    if (orderType === 'pickup' && !storeData) return false; // Both delivery and pickup need store data
    return date && time;
  };

  return (
    <div className="max-w-2xl mx-auto">

      <div className="flex mb-6 gap-2 text-white">
        <div
          role='button'
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleOrderTypeChange('delivery');
            }
          }}
          onClick={() => handleOrderTypeChange('delivery')}
          className={`
            flex-1 flex flex-col items-center justify-center rounded-xl
            shadow-md border-[1px] bg-[#5A3770]
             ${orderType === 'delivery' ? 'opacity-100 border-[#68477d]' : 'opacity-40 border-darkElevationPrimary'}
          `}
        >
          {orderType === 'delivery' ? (
            <Lottie animationData={truckAnimation} autoPlay loop height={100} width={100} className='md:h-[88px] md:w-[88px]' />
          ) : (
            <Lottie autoplay={false} animationData={truckAnimation} height={100} width={100} className='md:h-[88px] md:w-[88px]' />
          )}
          <span className='text-base font-bold font-montserratBold'>Delivery</span>
        </div>
        <div
          role='button'
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              handleOrderTypeChange('pickup');
            }
          }}
          onClick={() => handleOrderTypeChange('pickup')}
          className={`
            flex-1 flex flex-col items-center justify-center rounded-xl
            shadow-md border-[1px] bg-[#5A3770]
             ${orderType === 'pickup' ? 'opacity-100 border-[#68477d]' : 'opacity-40 border-darkElevationPrimary'}
          `}
        >
          {orderType === 'pickup' ? (
            <Lottie animationData={bakeryAnimation} autoPlay loop height={100} width={100} className='md:h-[88px] md:w-[88px]' />
          ) : (
            <Lottie autoplay={false} animationData={bakeryAnimation} height={100} width={100} className='md:h-[88px] md:w-[88px]' />
          )}
          <span className='text-base font-bold font-montserratBold'>Pickup</span>
        </div>
      </div>

      <div className="space-y-3 mb-8">
        <AddressAutocomplete
          value={address}
          onChange={handleAddressChange}
          onStoreSelect={handleStoreSelect}
          onStoresLoaded={handleStoresLoaded}
          onAddressSelect={handleAddressSelect}
          placeholder={orderType === 'delivery' ? "Enter delivery address *" : "Find a bakery near you *"}
          delivery={orderType === 'delivery'}
        />

        {/* Show stores dropdown only for pickup mode */}
        {availableStores.length > 0 && orderType === 'pickup' && (
          <div className="mt-4">
            <div className="relative" ref={storeDropdownRef}>
              <div
                className="border-[1px] border-[#5A3770] bg-[#2C0C37] rounded-[40px] flex gap-2 items-center justify-center px-2 min-h-[52px] pl-4 cursor-pointer"
                onClick={() => setShowStoreDropdown(!showStoreDropdown)}
                onKeyDown={handleStoreKeyDown}
                tabIndex={0}
                role="button"
                aria-haspopup="listbox"
                aria-expanded={showStoreDropdown}
              >
                <input
                  type="text"
                  className="text-[#B4A5BB] w-full bg-transparent outline-[0] h-11 text-base font-medium font-montserratMedium cursor-pointer"
                  value={storeData ? `${storeData.name} - ${storeData.address}${storeData.distanceToStore ? ` (${storeData.distanceToStore} miles)` : ''}${storeData.isPickupOpen ? ' - Open' : ' - Closed'}` : ''}
                  placeholder="Select Store *"
                  readOnly
                />
                <svg className="w-4 h-4 text-[#B4A5BB]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {showStoreDropdown && availableStores.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-[#2C0C37] border border-[#5A3770] rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {availableStores.map((store, index) => (
                    <div
                      key={store.id}
                      className={`px-4 py-3 cursor-pointer text-[#B4A5BB] hover:bg-[#5A3770] hover:text-white transition-colors ${
                        index === selectedStoreIndex ? 'bg-[#5A3770] text-white' : ''
                      }`}
                      onClick={() => {
                        handleStoreSelect(store);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleStoreSelect(store);
                        }
                      }}
                      onMouseEnter={() => setSelectedStoreIndex(index)}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="font-medium">
                        {store.name}
                         -
                        {store.address}
                      </div>
                      <div className="text-sm opacity-75">
                        {store.distanceToStore && `${store.distanceToStore} miles`}
                        {store.isPickupOpen ? ' - Open' : ' - Closed'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}


        <div className='relative border-[1px] border-[#5A3770] bg-[#2C0C37] rounded-[40px] flex gap-2 items-center justify-center px-2 min-h-[52px] pl-4'>
          <DatePicker
            showIcon
            className='text-[#B4A5BB] w-full bg-transparent outline-[0] h-11 text-base font-medium font-montserratMedium focus:outline-[0] focus-within:outline-[0]'
            selected={date}
            onChange={handleDateChange}
            dateFormat='MM/dd/yyyy'
            placeholderText="Pick a Date *"
            minDate={new Date()}
            maxDate={new Date(Date.now() + 6 * 30 * 24 * 60 * 60 * 1000)}
          />
        </div>

        <div className="relative" ref={timeDropdownRef}>
          <div
            className="border-[1px] border-[#5A3770] bg-[#2C0C37] rounded-[40px] flex gap-2 items-center justify-center px-2 min-h-[52px] pl-4 cursor-pointer"
            onClick={() => setShowTimeDropdown(!showTimeDropdown)}
            onKeyDown={handleTimeKeyDown}
            tabIndex={0}
            role="button"
            aria-haspopup="listbox"
            aria-expanded={showTimeDropdown}
          >
            <input
              type="text"
              className="text-[#B4A5BB] w-full bg-transparent outline-[0] h-11 text-base font-medium font-montserratMedium cursor-pointer"
              value={time && time !== '' ? generateTimeSlots().find(slot => slot.value === time || (time === 'asap' && slot.value === null))?.label || '' : ''}
              placeholder="Pick a Time *"
              readOnly
            />
            <svg className="w-4 h-4 text-[#B4A5BB]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          {showTimeDropdown && generateTimeSlots().length > 0 && (
            <div className="absolute z-50 w-full mt-1 bg-[#2C0C37] border border-[#5A3770] rounded-lg shadow-lg max-h-60 overflow-y-auto top-auto bottom-full">
              {generateTimeSlots().map((slot, index) => (
                <div
                  key={slot.value || `asap-${index}`}
                  className={`px-4 py-3 cursor-pointer text-[#B4A5BB] hover:bg-[#5A3770] hover:text-white transition-colors ${
                    index === selectedTimeIndex ? 'bg-[#5A3770] text-white' : ''
                  }`}
                  onClick={() => {
                    handleTimeChange(slot);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleTimeChange(slot);
                    }
                  }}
                  onMouseEnter={() => setSelectedTimeIndex(index)}
                  role="button"
                  tabIndex={0}
                >
                  {slot.label}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {errorMessage && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {errorMessage}
        </div>
      )}

      <div className="flex justify-between gap-4">
        <button
          type="button"
          className={`
          dark:bg-button px-4 items-center justify-center  flex w-auto bg-button text-white text-center py-2 h-11
            ${isFormValid()
              ? 'bg-purple-500 text-white hover:bg-purple-600 hover:shadow-lg hover:-translate-y-0.5'
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }
          `}
          onClick={handleNext}
          disabled={!isFormValid()}
        >
          Next
        </button>
      </div>
    </div>
  );
};

Event.propTypes = {
  formData: PropTypes.shape({
    orderType: PropTypes.string,
    address: PropTypes.string,
    date: PropTypes.instanceOf(Date),
    time: PropTypes.string,
    storeData: PropTypes.object,
    addressCoordinates: PropTypes.object
  }).isRequired,
  onFormDataChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
};

export default Event;
